import locales from "."
import { useAuthStore } from "@/stores/auth"
import { useEventsStore } from "@/stores/apps/events"

export type MessageSchema = typeof locales.en
export type Locales = keyof typeof locales

/**
 * Language configuration object containing all language-related data
 * - platform: All languages that the platform supports
 * - default: Default languages that all organizations have by default
 * - keys: Mapping from language codes to their translation keys in general namespace
 * - fallbackNames: Hardcoded fallback names for each language
 */
export const languagesConfig = {
	locales: {
		es:"es-ES",
		en:"en-US",
		ca:"ca-ES",
		it:"it-IT",
		fr:"fr-FR",
		de:"de-DE",
		jp:"ja-JP"
	},
	platform: ["en", "es", "ca", "it"] as const,
	defaultKeys: ["es", "en", "lang1", "lang2", "lang3"] as const,
	default: ["es", "en"] as const,
	keys: {
		es: "spanish",
		en: "english",
		ca: "catalan",
		it: "italian",
	} as const,
	fallbackNames: {
		es: "Español",
		en: "English",
		ca: "Català",
		it: "Italiano",
	} as const
} as const

export function getLocales(): Record<string, string> {
	return languagesConfig.locales
}

/**
 * Gets the locale string (e.g., "es-ES", "en-US") for a given language code
 * @param languageCode - Language code (e.g., "es", "ca", "en")
 * @returns Locale string (e.g., "es-ES", "ca-ES", "en-US") or "es-ES" as fallback
 */
export function getLocaleString(languageCode: string | null | undefined): string {
	if (!languageCode) {
		return languagesConfig.locales.es || "es-ES"
	}
	
	// Check if we have a specific locale mapping
	if (languageCode in languagesConfig.locales) {
		return languagesConfig.locales[languageCode as keyof typeof languagesConfig.locales]
	}
	
	// Fallback: return language code with "-ES" suffix for most languages
	// or "-US" for English
	if (languageCode === "en") {
		return "en-US"
	}
	return `${languageCode}-ES`
}

export const defaultKeys = languagesConfig.defaultKeys
/**
 * All languages that the platform supports
 * This is the complete list of languages available in the platform
 * Users/organizations can select up to 5 languages from this list
 * 
 * NOTE: Node.js scripts parse this file directly to extract the platform array.
 * The array is defined in languagesConfig.platform above.
 */
export const platformLanguages = languagesConfig.platform

/**
 * Gets platform languages array
 * For Node.js scripts: Parse config.ts and extract the platform array from languagesConfig.platform
 * Example regex: /platform:\s*\[(.*?)\]\s+as\s+const/s
 */
export function getPlatformLanguages(): readonly string[] {
	return platformLanguages
}

/**
 * Default languages that all organizations have by default
 * Organizations always have "es" and "en", plus up to 3 more from lang1/lang2/lang3 (max 5 total)
 */
export const defaultOrganizationLanguages = languagesConfig.default

/**
 * Gets all selectable languages for the organization (defaults + lang1/lang2/lang3)
 * These are the languages that can be selected in the MultiSelect
 */
export function getSelectableLanguages(): readonly string[] {
	const organization = getOrganization()
	
	if (!organization) {
		return []
	}
	
	const languages: string[] = []
	const seen = new Set<string>()
	
	const addLanguage = (code: unknown) => {
		if (typeof code !== 'string') return
		const trimmed = code.trim()
		if (!trimmed || seen.has(trimmed)) return
		seen.add(trimmed)
		languages.push(trimmed)
	}
	
	// Always include default languages: "es" and "en"
	defaultOrganizationLanguages.forEach(addLanguage)
	
	// Add languages from lang1, lang2, lang3 if they have values (these are available to select)
	for (const langKey of ['lang1', 'lang2', 'lang3'] as const) {
		addLanguage(organization[langKey])
	}
	
	// Limit to max 5 languages (es, en + up to 3 from lang1/lang2/lang3)
	return languages.slice(0, 5) as readonly string[]
}

/**
 * Gets currently selected languages from the user's organization
 */
export function getAvailableLanguages(): readonly string[] {
	const organization = getOrganization()
	
	if (!organization) {
		return []
	}

	const languagesRaw = Array.isArray(organization.languages) ? organization.languages : []
	const resolved: string[] = []
	const seen = new Set<string>()

	const orgConfiguredCodes = ['lang1', 'lang2', 'lang3']
		.map((key) => organization[key])
		.filter((code): code is string => typeof code === 'string' && code.trim() !== '')

	const addIfValid = (languageCode: unknown) => {
		if (typeof languageCode !== 'string') return
		const code = languageCode.trim()
		if (!code) return
		const isPlatformLanguage = platformLanguages.includes(code as any)
		const isOrgConfigured = orgConfiguredCodes.includes(code)
		if (!isPlatformLanguage && !isOrgConfigured) return
		if (seen.has(code)) return
		seen.add(code)
		resolved.push(code)
	}

	// languages may come from backend as keys (es/en/lang1/lang2/lang3) or as raw codes (es/en/ca).
	languagesRaw.forEach((langItem: unknown) => {
		if (typeof langItem !== 'string') return
		const item = langItem.trim()
		if (!item) return

		if (item === 'es' || item === 'en') {
			addIfValid(item)
			return
		}

		if (item === 'lang1' || item === 'lang2' || item === 'lang3') {
			addIfValid(organization[item])
			return
		}

		addIfValid(item)
	})

	// Fallback for old/incomplete organizations with empty languages array.
	if (resolved.length === 0) {
		addIfValid('es')
		addIfValid('en')
		addIfValid(organization.lang1)
		addIfValid(organization.lang2)
		addIfValid(organization.lang3)
	}

	return resolved as readonly string[]
}

// Returns the keys of the available languages selected in the organization es, en, lang1, lang2, lang3
export function getAvailableLanguagesKeys(): readonly string[] {
	const organization = getOrganization()
	if (!organization) {
		return []
	}
	
	// Get the languages array (e.g., ['es', 'ca', 'en'])
	const languages = getAvailableLanguages();
	const result: string[] = [];
	
	// Loop through all languages in the array (preserving order)
	for (const langValue of languages) {
		// If it's 'es' or 'en', add as text value
		if (langValue === 'es' || langValue === 'en') {
			result.push(langValue);
		} else {
			// Check if this value matches organization.lang1, lang2, or lang3
			for (const langKey of ['lang1', 'lang2', 'lang3']) {
				if (organization[langKey] === langValue) {
					result.push(langKey); // Return the key (lang1, lang2, or lang3)
					break; // Found a match, move to next language
				}
			}
			// If not found in lang1/lang2/lang3, skip it
		}
	}
	
	return result as readonly string[]
}

function getOrganization()
{
	try{
		const authStore = useAuthStore()
		// First try to get organization from auth store (when user is logged in)
		const orgFromAuth = (authStore.full_user as any)?.organization
		if (orgFromAuth) {
			return orgFromAuth
		}
		
		// If not available, try to get from events store (when user isn't connected but event is selected)
		const eventsStore = useEventsStore()
		const orgFromEvent = eventsStore.eventSelected?.organization
		if (orgFromEvent) {
			return orgFromEvent
		}
		
		return null
	} catch (error) {
		return null
	}
}


/**
 * Language name mappings for display
 * Returns an object with language codes as keys and translated names as values
 * Uses the user's language from authStore to get translations from general namespace
 * Falls back to hardcoded names if translations are not available
 */
export function getLanguagesText(): Record<string, string> {
	// Get user's language from auth store
	let userLanguage = "es" // default fallback
	try {
		const authStore = useAuthStore()
		userLanguage = (authStore.full_user as any)?.language || "es"
	} catch (error) {
		// Store might not be initialized yet
		userLanguage = "es"
	}
	
	// Ensure userLanguage is a valid locale key
	const validLocale = userLanguage in locales ? userLanguage : "es"
	const userLocale = locales[validLocale as keyof typeof locales] as any
	
	const langMap: Record<string, string> = {}
	
	// Build language map using translations from user's language
	for (const [langCode, translationKey] of Object.entries(languagesConfig.keys)) {
		// Try to get from general namespace first
		const generalTranslation = userLocale?.general?.[translationKey]
		// Fallback to root level if not in general
		const rootTranslation = userLocale?.[translationKey]
		// Use translation or fallback
		langMap[langCode] = generalTranslation || rootTranslation || languagesConfig.fallbackNames[langCode as keyof typeof languagesConfig.fallbackNames]
	}
	
	return langMap
}

/**
 * Gets languages array and text mapping
 * Convenience function that returns both languages and their display names
 */
export function getLanguages() {
	return {
		languages: [...getAvailableLanguages()] as string[],
		languagesText: getLanguagesText()
	}
}

/**
 * Gets i18n configuration
 * Note: The hardcoded "en" and "es" here are for initial locale detection only,
 * not for available languages. Available languages come from organization data via getAvailableLanguages()
 */
export function getI18NConf() {
	let locale = null;
	if( localStorage.getItem("language") != null)
	{
		locale = localStorage.getItem("language")
	}
	else
	{
		const userLang = navigator.language || (navigator as any).userLanguage;
		// Initial locale detection - defaults to "es" or "en" based on browser
		locale = userLang == 'en' || userLang == 'es' ? userLang : 
							localStorage.getItem("language") != undefined ? localStorage.getItem("language") == "Inglés" ? "en" : "es" 
						: 'es'
		localStorage.setItem("language", locale == "en" ? "en" : "es")

	}
	return {
		legacy: false,
		locale:  locale,
		messages: locales
	}
}

/**
 * Language selection utilities for components that need to manage selected languages
 */

/**
 * Default storage key for selected languages (shared across all components)
 */
export const SELECTED_LANGUAGES_STORAGE_KEY = 'selectedLanguages'

/**
 * Loads selected languages from localStorage
 * @param availableLanguages - Array of available language codes
 * @param maxLanguages - Maximum number of languages to return (default: 2)
 * @param storageKey - Optional localStorage key (defaults to SELECTED_LANGUAGES_STORAGE_KEY)
 * @returns Array of selected language codes
 */
export function loadSelectedLanguagesFromStorage(
	availableLanguages: readonly string[],
	maxLanguages: number = 2,
	storageKey: string = SELECTED_LANGUAGES_STORAGE_KEY
): string[] {
	const stored = localStorage.getItem(storageKey)
	if (stored) {
		try {
			const parsed = JSON.parse(stored)
			// Filter to only include languages that are still available
			const valid = parsed.filter((lang: string) => availableLanguages.includes(lang))
			if (valid.length >= maxLanguages) {
				return valid.slice(0, maxLanguages)
			} else if (valid.length === 1 && availableLanguages.length >= maxLanguages) {
				// If only 1 valid stored language, add the first available that's not the stored one
				const otherLang = availableLanguages.find(lang => lang !== valid[0])
				if (otherLang) {
					return [valid[0], otherLang]
				}
			} else if (valid.length > 0) {
				return valid
			}
		} catch (e) {
			console.error('Error parsing stored languages:', e)
		}
	}
	// If no valid stored languages, use first N available (or all if less than maxLanguages)
	const selected = availableLanguages.slice(0, Math.min(maxLanguages, availableLanguages.length))
	// Ensure we have maxLanguages if available
	if (selected.length < maxLanguages && availableLanguages.length >= maxLanguages) {
		// Fill with first available that's not already selected
		for (const lang of availableLanguages) {
			if (selected.length >= maxLanguages) break
			if (!selected.includes(lang)) {
				selected.push(lang)
			}
		}
	}
	return selected
}

/**
 * Saves selected languages to localStorage
 * @param languages - Array of language codes to save
 * @param storageKey - Optional localStorage key (defaults to SELECTED_LANGUAGES_STORAGE_KEY)
 */
export function saveSelectedLanguagesToStorage(
	languages: string[],
	storageKey: string = SELECTED_LANGUAGES_STORAGE_KEY
): void {
	localStorage.setItem(storageKey, JSON.stringify(languages))
}

/**
 * Ensures we have exactly maxLanguages selected from available languages
 * @param currentSelected - Currently selected languages
 * @param availableLanguages - Array of available language codes
 * @param maxLanguages - Maximum number of languages (default: 2)
 * @returns Array of selected language codes (guaranteed to have maxLanguages if available)
 */
export function ensureTwoLanguages(
	currentSelected: string[],
	availableLanguages: readonly string[],
	maxLanguages: number = 2
): string[] {
	// Filter to only include languages that are still available
	const valid = currentSelected.filter(lang => availableLanguages.includes(lang))
	
	if (valid.length < maxLanguages && availableLanguages.length >= maxLanguages) {
		// Rebuild the selected languages array
		const newLangs: string[] = []
		// Keep valid ones
		newLangs.push(...valid)
		// Add missing ones from available
		for (const lang of availableLanguages) {
			if (newLangs.length >= maxLanguages) break
			if (!newLangs.includes(lang)) {
				newLangs.push(lang)
			}
		}
		return newLangs
	} else if (valid.length !== currentSelected.length) {
		return valid
	}
	return valid
}

/**
 * Gets language options for a dropdown, excluding a specific language
 * @param availableLanguages - Array of available language codes
 * @param excludeLanguage - Language code to exclude from options
 * @param languagesText - Object mapping language codes to display names
 * @returns Array of options with label and value properties
 */
export function getLanguageDropdownOptions(
	availableLanguages: readonly string[],
	excludeLanguage: string | undefined,
	languagesText: Record<string, string>
): Array<{ label: string; value: string }> {
	return availableLanguages
		.filter(lang => lang !== excludeLanguage)
		.map(lang => ({
			label: languagesText[lang] || lang,
			value: lang
		}))
}

/**
 * Gets dropdown options for available languages in the organization
 * Convenience function that automatically gets available languages and their display names
 * @param excludeLanguage - Optional language code to exclude from options
 * @returns Array of options with label and value properties suitable for dropdowns/selects
 */
export function getAvailableLanguagesDropdownOptions(
	excludeLanguage?: string
): Array<{ label: string; value: string }> {
	const availableLanguages = getAvailableLanguages()
	const languagesText = getLanguagesText()
	return getLanguageDropdownOptions(availableLanguages, excludeLanguage, languagesText)
}

/**
 * Gets dropdown options for a specific array of available languages
 * Filters the language options based on the provided available_languages array
 * @param available_languages - Array of language codes (e.g., ["es", "ca", "en"])
 * @param excludeLanguage - Optional language code to exclude from options
 * @returns Array of options with label and value properties suitable for dropdowns/selects
 */
export function getLanguagesDropdownOptionsFromArray(
	available_languages: string[],
	excludeLanguage?: string
): Array<{ label: string; value: string }> {
	const languagesText = getLanguagesText()
	return getLanguageDropdownOptions(available_languages, excludeLanguage, languagesText)
}

/**
 * Handles language change with swap logic
 * If the new language is already selected in another position, swaps them
 * @param currentSelected - Currently selected languages array (will be mutated)
 * @param newLang - New language code to set
 * @param index - Index in the array to update
 * @returns Updated array of selected languages
 */
export function handleLanguageChange(
	currentSelected: string[],
	newLang: string,
	index: number
): string[] {
	const otherIndex = index === 0 ? 1 : 0
	if (currentSelected[otherIndex] === newLang) {
		// Swap the languages
		const temp = currentSelected[index]
		currentSelected[index] = currentSelected[otherIndex]
		currentSelected[otherIndex] = temp
	} else {
		currentSelected[index] = newLang
	}
	return currentSelected
}

/**
 * Converts a single language value to its corresponding key
 * Converts language value (like 'es', 'ca', 'en') to key (like 'es', 'lang1', 'en')
 * @param languageValue - Language value (e.g., 'es', 'en', 'ca', 'it')
 * @returns Language key or the value itself if not found
 */
export function mapLanguageValueToKey(
	languageValue: string | null | undefined
): string | null {
	if (!languageValue) {
		return null
	}
	
	const organization = getOrganization()
	
	if (!organization) {
		return languageValue
	}
	
	// For 'es' and 'en', return the value directly (they are both key and value)
	if (languageValue === 'es' || languageValue === 'en') {
		return languageValue
	}
	
	// For other languages, check if they match lang1, lang2, or lang3
	for (const langKey of ['lang1', 'lang2', 'lang3']) {
		if (organization[langKey] === languageValue) {
			return langKey // Return the key (lang1, lang2, or lang3)
		}
	}
	
	// If not found, return the value as fallback
	return languageValue
}

/**
 * Maps selected language values to their corresponding keys
 * Converts language values (like 'es', 'ca', 'en') to keys (like 'es', 'lang1', 'en')
 * @param selectedLanguages - Array of selected language values
 * @returns Array of language keys
 */
export function mapLanguageValuesToKeys(
	selectedLanguages: string[]
): string[] {
	return selectedLanguages
		.map(langValue => mapLanguageValueToKey(langValue))
		.filter((key): key is string => key !== null)
}

/**
 * Converts a language key to its corresponding value
 * Converts language keys (like 'es', 'en', 'lang1', 'lang2', 'lang3') to values (like 'es', 'en', 'ca', 'it')
 * @param languageKey - Language key (e.g., 'es', 'en', 'lang1', 'lang2', 'lang3')
 * @returns Language value or the key itself if not found
 */
export function mapLanguageKeyToValue(
	languageKey: string | null | undefined
): string | null {
	if (!languageKey) {
		return null
	}
	
	const organization = getOrganization()
	
	if (!organization) {
		return languageKey
	}
	
	// For 'es' and 'en', return the key directly (they are both key and value)
	if (languageKey === 'es' || languageKey === 'en') {
		return languageKey
	}
	
	// For lang1, lang2, lang3, get the actual language value from organization
	if (['lang1', 'lang2', 'lang3'].includes(languageKey)) {
		return organization[languageKey] || null
	}
	
	// If it's already a value (like 'ca', 'it'), return it as is
	return languageKey
}

/**
 * Gets the default language from the organization
 * Returns the actual language code (e.g., "ca", "it", "es", "en")
 * Falls back to "es" if no default language is set or if organization is not available
 * @returns Default language code or "es" as fallback
 */
export function getDefaultLanguage(): string {
	const organization = getOrganization()
	if (organization?.default_language) {
		const defaultLang = mapLanguageKeyToValue(organization.default_language)
		return defaultLang || "es"
	}
	return "es"
}